import { type FieldErrors, type FieldValues } from 'react-hook-form';

import { type FlowListName } from '@/components/Wizard';

import { FORM_START_STEPS, FORM_START_STORAGE_PREFIX, GTM_EVENTS, GTM_FORM_NAME, type GtmFlow } from './constants';
import { pushToDataLayer } from './dataLayer';
import {
  buildStepTrackingContext,
  flattenFieldErrors,
  getLastTrackedStep,
  getValueAtPath,
  mapFieldErrorType,
  resolveValidationErrorTypes,
  setLastTrackedStep,
  type StepNumberRegistry,
  type TrackedStepContext
} from './map';
import { hasCompletedStep, markCompletedStep, resolveTrackingUserId } from './stepCompletion';

export type TrackStepOptions = {
  flowIndex: number;
  isSkipped?: boolean;
  registry?: StepNumberRegistry;
};

let lastPushedUserKey: string | undefined;
const formStartSentForUser = new Set<string>();

const baseFormPayload = (flow: GtmFlow | undefined) => ({
  form_name: GTM_FORM_NAME,
  flow
});

const baseStepPayload = (context: TrackedStepContext) => ({
  ...baseFormPayload(context.flow),
  step_number: context.stepNumber,
  step_name: context.stepName
});

const isSameTrackedStep = (left: TrackedStepContext, right: TrackedStepContext) =>
  left.stepName === right.stepName && left.stepNumber === right.stepNumber && left.flow === right.flow;

export const trackUserIdentity = (userId: string, belt: string | null) => {
  const key = `${userId}:${belt ?? ''}`;

  if (lastPushedUserKey === key) {
    return;
  }

  lastPushedUserKey = key;
  pushToDataLayer({
    user_id: userId,
    belt
  });
};

export const trackFormStart = (
  stepName: string,
  currentFlowList: FlowListName | undefined,
  flowlistHistory: ReadonlySet<FlowListName> | undefined,
  options?: TrackStepOptions
) => {
  if (!FORM_START_STEPS.has(stepName)) {
    return;
  }

  if (options?.isSkipped) {
    return;
  }

  if (
    flowlistHistory?.has('document-not-found') &&
    (stepName === 'face-detection' || stepName === 'legal-entity-relation')
  ) {
    return;
  }

  const context = buildStepTrackingContext(
    stepName,
    currentFlowList,
    flowlistHistory,
    options?.flowIndex ?? 0,
    options?.registry
  );

  if (!context) {
    return;
  }

  const userId = resolveTrackingUserId();
  const storageKey = `${FORM_START_STORAGE_PREFIX}${userId}`;

  if (formStartSentForUser.has(userId)) {
    return;
  }

  try {
    if (localStorage.getItem(storageKey)) {
      formStartSentForUser.add(userId);
      return;
    }
  } catch {
    console.error('Error reading localStorage', storageKey);
  }

  formStartSentForUser.add(userId);

  try {
    localStorage.setItem(storageKey, '1');
  } catch {
    console.error('Error writing localStorage', storageKey);
  }

  pushToDataLayer({
    event: GTM_EVENTS.formStart,
    ...baseFormPayload(context.flow)
  });
};

export const trackStepView = (
  stepName: string,
  currentFlowList: FlowListName | undefined,
  flowlistHistory: ReadonlySet<FlowListName> | undefined,
  options: TrackStepOptions
) => {
  const context = buildStepTrackingContext(
    stepName,
    currentFlowList,
    flowlistHistory,
    options.flowIndex,
    options.registry
  );

  if (!context) {
    return;
  }

  const last = getLastTrackedStep();

  if (last && isSameTrackedStep(last, context)) {
    return;
  }

  setLastTrackedStep(context);

  pushToDataLayer({
    event: GTM_EVENTS.stepView,
    ...baseStepPayload(context)
  });

  trackFormStart(stepName, currentFlowList, flowlistHistory, options);
};

export const trackStepComplete = (
  stepName?: string,
  currentFlowList?: FlowListName,
  flowlistHistory?: ReadonlySet<FlowListName>,
  options?: TrackStepOptions
) => {
  const context = stepName
    ? buildStepTrackingContext(stepName, currentFlowList, flowlistHistory, options?.flowIndex ?? 0, options?.registry)
    : getLastTrackedStep();

  if (!context) {
    return;
  }

  setLastTrackedStep(context);

  if (hasCompletedStep(context.stepName)) {
    return;
  }

  markCompletedStep(context.stepName);

  pushToDataLayer({
    event: GTM_EVENTS.stepComplete,
    ...baseStepPayload(context)
  });
};

export const trackFieldErrors = (
  errors: FieldErrors<FieldValues>,
  values: FieldValues,
  stepName: string,
  currentFlowList: FlowListName | undefined,
  flowlistHistory: ReadonlySet<FlowListName> | undefined,
  options: TrackStepOptions
) => {
  const context = buildStepTrackingContext(
    stepName,
    currentFlowList,
    flowlistHistory,
    options.flowIndex,
    options.registry
  );

  if (!context) {
    return;
  }

  for (const { fieldName, error } of flattenFieldErrors(errors)) {
    pushToDataLayer({
      event: GTM_EVENTS.fieldError,
      ...baseStepPayload(context),
      field_name: fieldName,
      error_type: mapFieldErrorType(error, getValueAtPath(values, fieldName))
    });
  }
};

export const trackValidationError = (error: unknown, fallbackSubtitle?: unknown) => {
  const context = getLastTrackedStep();

  if (!context) {
    return;
  }

  for (const errorType of resolveValidationErrorTypes(error, fallbackSubtitle)) {
    pushToDataLayer({
      event: GTM_EVENTS.validationError,
      ...baseStepPayload(context),
      error_type: errorType
    });
  }
};

export const trackVerificationResult = (
  result: 'aprovado' | 'em_analise',
  currentFlowList: FlowListName | undefined,
  flowlistHistory: ReadonlySet<FlowListName> | undefined,
  stepName: string
) => {
  const context = buildStepTrackingContext(stepName, currentFlowList, flowlistHistory, 0);

  pushToDataLayer({
    event: GTM_EVENTS.verificationResult,
    ...baseFormPayload(context?.flow),
    result
  });
};
